import * as angular from "angular";
import {IController, ITimeoutService} from "angular";
import * as _ from "lodash";
import {MapsCommonService} from "../../services/mapsCommon.service";
import {MapPolygonDrawService, MapPolygonDrawSession} from "../../services/map-polygon-draw.service";
import {AccountModel} from "../../../account/angular/models/account.model";

export class FeaturesMapComponent implements IController {
  features: any;
  depots: any;
  accounts: AccountModel[];
  selectedFeature: any;
  editingFeature: any;
  enableSearch: any;
  onSelectFeature: Function;
  onChange: Function;
  onReady: Function;

  drawSession: MapPolygonDrawSession;
  private map: any;
  private static: boolean = true;
  private mapPolygons: any = [];
  private polygonConfig: any = {
    clickable: true
  };
  private polygonEvents: any;
  private editablePolygonEvents: any;
  private mapPolygonsControl: any = {};
  private editablePolygons: any = [];
  private addressMarkers: any = [];
  private mapInstance: google.maps.Map;
  private savedPolygon: any = undefined;
  private isReady: boolean;
  public accountsMarkersLayer: any;
  public depotsMarkersLayer: any;
  private depotInfoWindow: any;
  private TOOLTIP_TIMEOUT_MS: number = 500;
  private depotInfoWindowModel: any;
  public polylines: any;
  public filters: any;

  constructor(private uiGmapIsReady, private mapsCommonService: MapsCommonService, private mapPolygonDrawService: MapPolygonDrawService, private $timeout: ITimeoutService) {
  }

  $onInit() {
    this.drawSession = this.mapPolygonDrawService.createSession({
      $timeout: this.$timeout,
      onDrawFinish: (coordinates) => {
        this._onDrawFinish(coordinates);
      },
      onDrawDelete: () => {
        this.$timeout(0).then(() => {
          this.editablePolygons = [];
          this.onChange();
        });
      }
    });

    this.depotInfoWindow = this.mapsCommonService.initActiveWindow();
    this.filters = {
      serviceAreas: true
    };

    this.map = {
      isReady: false,
      center: this.mapsCommonService.getSiteLocation(),
      zoom: 12,
      events: {
        idle: (map) => {
          if (!this.isReady) {
            this.isReady = true;
            google.maps.event.trigger(map, 'resize');
            this.centerMap(this.features);
          }
        }
      },
      options: {
        styles: this.mapsCommonService.getDefaultStyle(),
        scaleControl: true,
        mapTypeControl: false
      },
      bounds: {},
      accountsMarkersLayerEnabled: true,
      depotsMarkersLayerEnabled: true,
      accountsMarkersLayerCallback: (layer) => {
        this.accountsMarkersLayer = layer;

        if (this.accounts?.length) {
          this._setAccounts(this.accounts);
        }

        this.accountsMarkersLayer.addListener("mouseover", (event) => {
          const account = event.feature.getProperty("ref");
          this.depotInfoWindow.isFocused = true;
          this.$timeout(this.TOOLTIP_TIMEOUT_MS).then(() => {
            if (!this.depotInfoWindow.show && this.depotInfoWindow.isFocused) {
              this.depotInfoWindow.coords = _.get(account, 'address.location');
              this.depotInfoWindow.show = true;
              this.depotInfoWindowModel = account;
            }
          });
        });

        this.accountsMarkersLayer.addListener("mouseout", (event) => {
          this.$timeout(0).then(() => {
            if (this.depotInfoWindow) {
              this.depotInfoWindow.show = false;
              this.depotInfoWindow.isFocused = false;
            }
            this.depotInfoWindowModel = undefined;
          });
        });

      },
      depotsMarkersLayerCallback: (layer) => {
        this.depotsMarkersLayer = layer;

        if (this.depots?.length) {
          this._setDepots(this.depots);
        }

        this.depotsMarkersLayer.addListener("mouseover", (event) => {
          const depot = event.feature.getProperty("ref");
          this.depotInfoWindow.isFocused = true;
          this.$timeout(this.TOOLTIP_TIMEOUT_MS).then(() => {
            if (!this.depotInfoWindow.show && this.depotInfoWindow.isFocused) {
              this.depotInfoWindow.coords = _.get(depot, 'address.location');
              this.depotInfoWindow.show = true;
              this.depotInfoWindowModel = depot;
            }
          })
        });

        this.depotsMarkersLayer.addListener("mouseout", (event) => {
          this.$timeout(0).then(() => {
            if (this.depotInfoWindow) {
              this.depotInfoWindow.show = false;
              this.depotInfoWindow.isFocused = false;
            }
            this.depotInfoWindowModel = undefined;
          });
        });

      },
      control: {}
    };
    this.polygonEvents = {
      click: (feature, eventName, model) => {
        if (this.editingFeature) {
          return;
        }
        const selectedFeature = _.find(this.features, (mapFeature) => {
          return (mapFeature.properties.areaId || mapFeature.properties.id) === model.id;
        });
        if (selectedFeature) {
          this.onSelectFeature({feature: selectedFeature})
        }
      }
    };
    this.editablePolygonEvents = this.mapPolygonDrawService.buildEditablePolygonEvents(
      this.$timeout,
      () => this._toggleEditablePolygonEditMode(),
      (coordinates) => {
        this.onChange({coordinates: coordinates});
      }
    );

    this.uiGmapIsReady.promise(1).then((maps) => {
      this._whenMapReady(() => {
        this.mapInstance = this.map.control.getGMap();
        this.drawSession.setMapInstance(this.mapInstance);
        this.onReady();
        this._syncDrawingStateAfterMapReady();
      });
      this.centerMap(this.features);
    });

    if (this.features) {
      this.setMapPolygons(this.features);
      this.centerMap(this.features);
    }
  }

  $onDestroy() {
    this.drawSession.destroy();
  }

  $onChanges(changes) {
    if (changes.features && !changes.features.isFirstChange()) {
      const isDrawing = this.drawSession && this.drawSession.showDrawToolbar;
      this.setMapPolygons(this.features, isDrawing ? () => {
        this._applyBackgroundPolygonsClickable(false);
      } : undefined);

      const currentFeatures = changes.features.currentValue,
        previousFeatures = changes.features.previousValue;
      if (currentFeatures?.length !== previousFeatures?.length) {
        this.centerMap(this.features);
      }
    }

    if (changes.depots && this.depots?.length) {
      let currentValues = _.get(changes.depots, 'currentValue', []),
        previousValues = _.get(changes.depots, 'previousValue', []),
        depotsToRemove = _.differenceBy(previousValues, currentValues, 'id'),
        depotsToAdd = _.difference(currentValues, previousValues, 'id');


      if (this.depotsMarkersLayer) {
        if (depotsToRemove.length) {
          const idsToRemove = _.map(depotsToRemove, 'id');
          this.depotsMarkersLayer.forEach((feature) => {
            const id = feature.getProperty('id');
            if (_.includes(idsToRemove, id)) {
              this.depotsMarkersLayer.remove(feature);
            }
          });
        }

        if (depotsToAdd.length) {
          this._setDepots(depotsToAdd);
        }
      }

    }

    if (changes.accounts && this.accounts?.length) {
      let currentValues = _.get(changes.accounts, 'currentValue', []),
        previousValues = _.get(changes.accounts, 'previousValue', []),
        accountsToRemove = _.differenceBy(previousValues, currentValues, 'id'),
        accountsToAdd = _.difference(currentValues, previousValues, 'id');


      if (this.accountsMarkersLayer) {
        if (accountsToRemove.length) {
          const idsToRemove = _.map(accountsToRemove, 'id');
          this.accountsMarkersLayer.forEach((feature) => {
            const id = feature.getProperty('id');
            if (_.includes(idsToRemove, id)) {
              this.accountsMarkersLayer.remove(feature);
            }
          });
        }

        if (accountsToAdd.length) {
          this._setAccounts(accountsToAdd);
        }
      }

    }

    if (changes.editingFeature && (!changes.editingFeature.isFirstChange() || !_.isUndefined(changes.editingFeature.currentValue))) {
      this.setEditingFeature(changes.editingFeature.currentValue);
    }

    if (changes.selectedFeature && !changes.selectedFeature.isFirstChange()) {
      const selectedId = this.selectedFeature ? this.mapsCommonService.getFeaturePolygonId(this.selectedFeature) : undefined;
      this.mapPolygons = this.mapsCommonService.buildFeaturePolygons(this.features, true, false, selectedId, this.polygonConfig.clickable);
      if (this.selectedFeature) {
        this.map.bounds = this.mapsCommonService.getCoordinatesBounds(this.selectedFeature.geometry.coordinates[0]);
      }
    }
  }

  centerMap(features) {
    if (features) {
      const coordinates = _.flatten(_.map(features, (feature) => {
        return feature.geometry.coordinates[0];
      }));
      if (coordinates && coordinates.length) {
        this.map.bounds = this.mapsCommonService.getCoordinatesBounds(coordinates);
      }
    } else {
      this.map.center = this.mapsCommonService.getSiteLocation();
    }
  }

  onAddressChange(address) {
    if (address && address.placeId) {
      if (this.addressMarkers.length) {
        this.addressMarkers[0].coords = address.location;
      } else {
        this.addressMarkers = this.mapsCommonService.buildMarkersByAddresses([address], '#c5c5c5', false);
      }

      this.map.zoom = 15;
      this.map.center = angular.copy(address.location);
    } else {
      this.addressMarkers = [];
      this.centerMap(this.features);
    }
  }

  // ******************************
  //        Private methods
  // ******************************

  private setMapPolygons(features, onMounted?: () => void) {
    const selectedId = this.selectedFeature ? this.mapsCommonService.getFeaturePolygonId(this.selectedFeature) : undefined;
    const polygons = this.mapsCommonService.buildFeaturePolygons(features, true, false, selectedId, this.polygonConfig.clickable);
    this.mapPolygons = [];
    this.$timeout(0).then(() => {
      this.mapPolygons = polygons;
      if (onMounted) {
        onMounted();
      }
    });
  }

  private _whenMapReady(callback, attempt = 0) {
    const control = this.map && this.map.control;
    if (control && _.isFunction(control.getGMap) && control.getGMap()) {
      callback();
      return;
    }
    if (attempt >= 40) {
      return;
    }
    this.$timeout(50).then(() => this._whenMapReady(callback, attempt + 1));
  }

  private _toggleEditablePolygonEditMode() {
    const ref = this.editablePolygons[0];
    ref.static = !ref.static;
    ref.draggable = ref.static;
    this.editablePolygons = [];
    this.$timeout(100).then(() => {
      this.editablePolygons.push(ref);
    });
  }

  private _onDrawFinish(coordinates) {
    if (!this.editingFeature) {
      return;
    }

    this.editingFeature.geometry.coordinates = [coordinates];
    const polygonModel = this.mapsCommonService.buildFeaturePolygon(this.editingFeature, true, true, true, false);
    if (!polygonModel.id || !this.mapsCommonService.isValidPolygonRing(this.mapsCommonService.getFeaturePolygonRing({geometry: polygonModel.path}))) {
      this.toggleShowDrawingTools(true);
      return;
    }

    this.drawSession.prepareForEditablePolygon();
    this._setEditablePolygons([polygonModel], () => {
      this.drawSession.finalizeAfterEditablePolygonMounted();
      this.polygonConfig.clickable = true;
      this._applyBackgroundPolygonsClickable(true);
      this.onChange({coordinates: coordinates});
    });
  }

  private setEditingFeature(currentEditingFeature) {
    if (_.isNull(currentEditingFeature)) {
      this.resetEditMode();
      if (this.features?.length) {
        this.setMapPolygons(this.features);
      }
    }
    else if (_.isUndefined(currentEditingFeature)) {
      this.revertEditMode();
    }
    else {
      this.editablePolygons = [];
      this.drawSession.stopDrawing();
      this.drawSession.showDeleteToolOverlay(false);
      const editingPolygonId = this.mapsCommonService.getFeaturePolygonId(currentEditingFeature);
      const currentPolygonIndex = _.findIndex(this.mapPolygons, (polygon: any) => {
        return polygon.id === editingPolygonId;
      });
      if (currentPolygonIndex !== -1) {
        this.savedPolygon = this.mapPolygons[currentPolygonIndex];
        this.mapPolygons.splice(currentPolygonIndex, 1);
      }
      if (this.mapsCommonService.hasFeaturePolygonCoordinates(currentEditingFeature)) {
        this._setEditablePolygons([
          this.mapsCommonService.buildFeaturePolygon(currentEditingFeature, true, true, true, true)
        ], () => {
          this.drawSession.showDeleteToolOverlay(true);
        });
        this.centerMap([currentEditingFeature]);
      } else {
        this.toggleShowDrawingTools(true);
      }
    }
  }

  private revertEditMode() {
    if (this.savedPolygon) {
      this.mapPolygons.push(this.savedPolygon);
    }
    this.resetEditMode();
  }

  private resetEditMode() {
    this.drawSession.showDeleteToolOverlay(false);
    this.toggleShowDrawingTools(false);
    this.editablePolygons = [];
    this.savedPolygon = undefined;
  }

  private toggleShowDrawingTools(isEnabled) {
    // ui-gmap does not watch `clickable`, so a model rebuild never re-applies it
    // to already-rendered polygons. Flip it directly on the live gObjects instead;
    // otherwise large background polygons keep swallowing the drawing clicks.
    this.polygonConfig.clickable = !isEnabled;
    if (isEnabled) {
      this._applyBackgroundPolygonsClickable(false, () => {
        this.drawSession.startDrawing();
      });
    } else {
      this.drawSession.stopDrawing();
      this._applyBackgroundPolygonsClickable(true);
    }
  }

  private _applyBackgroundPolygonsClickable(isClickable: boolean, onApplied?: () => void, attempt = 0) {
    const getPlurals = _.get(this.mapPolygonsControl, 'getPlurals');
    const plurals = _.isFunction(getPlurals) ? getPlurals() : undefined;
    const expectedCount = this.mapPolygons ? this.mapPolygons.length : 0;
    const builtCount = plurals ? (plurals.length || 0) : 0;

    // ui-gmap builds the polygon gObjects asynchronously, so wait until they
    // exist before toggling clickable; otherwise drawing clicks get swallowed.
    if (expectedCount > 0 && builtCount < expectedCount && attempt < 20) {
      this.$timeout(25).then(() => this._applyBackgroundPolygonsClickable(isClickable, onApplied, attempt + 1));
      return;
    }

    const setClickable = (child) => {
      const polygon = child && child.gObject;
      if (polygon && _.isFunction(polygon.setOptions)) {
        polygon.setOptions({clickable: isClickable});
      }
    };
    if (plurals && _.isFunction(plurals.each)) {
      plurals.each(setClickable);
    } else {
      _.forEach(plurals, setClickable);
    }
    if (onApplied) {
      onApplied();
    }
  }

  private _syncDrawingStateAfterMapReady() {
    if (this.editingFeature && !this.mapsCommonService.hasFeaturePolygonCoordinates(this.editingFeature)) {
      this.toggleShowDrawingTools(true);
    }
  }

  private _setEditablePolygons(polygonModels, onMounted?: () => void) {
    const validModels = _.filter(polygonModels, (model) => {
      if (!model || !model.id || !model.path) {
        return false;
      }
      const ring = this.mapsCommonService.getFeaturePolygonRing({geometry: model.path});
      return this.mapsCommonService.isValidPolygonRing(ring);
    });
    if (!validModels.length) {
      this.toggleShowDrawingTools(true);
      return;
    }
    this.editablePolygons = [];
    this.$timeout(0).then(() => {
      this.editablePolygons = validModels;
      this._refreshMapAfterPolygonChange();
      if (onMounted) {
        onMounted();
      }
    });
  }

  private _refreshMapAfterPolygonChange() {
    if (this.mapInstance) {
      google.maps.event.trigger(this.mapInstance, 'resize');
    }
  }

  private _setAccounts(agents) {
    if (this.accountsMarkersLayer) {
      const geoJson = this.mapsCommonService.convertAccountsToGeoJson(agents);

      this.accountsMarkersLayer.addGeoJson(geoJson);

      this.accountsMarkersLayer.setStyle((feature) => {
        return this.getAccountFeatureStyle(feature);
      });
    }
  }

  private _setDepots(depots) {
    if (this.depotsMarkersLayer) {
      const geoJson = this.mapsCommonService.convertDepotsToGeoJson(depots);

      this.depotsMarkersLayer.addGeoJson(geoJson);

      this.depotsMarkersLayer.setStyle((feature) => {
        return this.getDepotFeatureStyle(feature);
      });
    }
  }

  private getAccountFeatureStyle(feature) {
    return {
      visible: true,
      icon: this.mapsCommonService.getAccountIcon()
    };
  }

  private getDepotFeatureStyle(feature) {
    const displayColor =  feature.getProperty('displayColor');
    return {
      visible: true,
      icon: this.mapsCommonService.buildDepotTaskIcon(false, displayColor)
    };
  }

}

angular
  .module('bringoz.admin.map')
  .component('featuresMap', {
    bindings: {
      features: '<',
      depots: '<?',
      accounts: '<?',
      selectedFeature: '<',
      editingFeature: '<',
      enableSearch: '<?',
      onSelectFeature: '&',
      onChange: '&',
      onReady: '&'
    },
    template: require('./features-map.html').default,
    controller: FeaturesMapComponent
  });
